import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AdminEmailChanges = () => {
    const token = useSelector((state) => state.user.token);
    const appNumber = useSelector((state) => state.app.appNumber);

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [source, setSource] = useState('');
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const limit = 50;

    useEffect(() => {
        const fetchEmailChanges = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({
                    page: String(page),
                    limit: String(limit),
                    appNumber: String(appNumber),
                });
                if (source) {
                    params.set('source', source);
                }

                const response = await fetch(`${apiPrefix}admin/email-changes?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'X-App-Number': String(appNumber),
                    },
                });

                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.error || 'Erreur lors de la récupération des changements d\'email');
                }

                const result = await response.json();
                setData(result.data || []);
                setTotal(result.total || 0);
            } catch (err) {
                console.error('Erreur fetchEmailChanges:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEmailChanges();
    }, [token, appNumber, source, page]);

    const formatDate = (value) =>
        value ? new Date(value).toLocaleString('fr-FR') : '';

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <Link to="/dashboard" className="text-theme-primary hover:underline mb-2 inline-block">
                        <i className="fas fa-arrow-left mr-2"></i>Tableau de bord
                    </Link>
                    <h1 className="text-4xl font-bold text-theme-primary">
                        Changements d'email
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Historique des 12 derniers mois (utilisateur et administrateur)
                    </p>
                </div>

                <div className="card-theme mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filtrer par origine
                    </label>
                    <select
                        value={source}
                        onChange={(event) => {
                            setPage(1);
                            setSource(event.target.value);
                        }}
                        className="input-theme max-w-xs"
                    >
                        <option value="">Tous</option>
                        <option value="user">Utilisateur</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
                        <span className="ml-3 text-gray-600">Chargement de l'historique...</span>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {error}
                    </div>
                ) : data.length === 0 ? (
                    <div className="card-theme p-8 text-center">
                        <i className="fas fa-envelope text-5xl text-gray-300 mb-4"></i>
                        <p className="text-gray-600 text-lg">
                            Aucun changement d'email enregistré pour le moment.
                        </p>
                    </div>
                ) : (
                    <div className="card-theme overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compte</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ancien email</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nouvel email</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origine</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {formatDate(row.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {row.userId} — {row.firstname} {row.lastname}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {row.oldEmail}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {row.newEmail}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {row.source === 'admin' ? 'Admin' : 'Utilisateur'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {row.source === 'admin'
                                                    ? `${row.adminFirstname} ${row.adminLastname}`.trim() || row.adminEmail
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-6 py-4 text-sm text-gray-500">
                            {total} changement(s) — page {page}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminEmailChanges;
